<template>
  <div>
    <v-sheet
      v-if="!roles.length"
      :color="`grey`"
      class="pa-3"
    >
      <v-skeleton-loader
        class="mx-auto"
        type="text-field, table, data-table"
      >
      </v-skeleton-loader>
    </v-sheet>
    <v-card v-else>
      <snack-view
        :message="snackbar.message"
        :activate="snackbar.active"
        :bg-color="snackbar.color"
      ></snack-view>
      <div>
        <v-card-title class="text-uppercase font-weight-bold">
          Liste des rôles
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-row>
            <v-col
              cols="12"
              offset-md="8"
              md="4"
            >
              <v-text-field
                v-model="search"
                :append-icon="icons.mdiMagnify"
                label="Rechercher"
                single-line
                hide-details
                dense
                outlined
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>

        <!-- Table -->
        <v-data-table
          :headers="headers"
          :items="roles"
          :search="search"
          :options="dataOptions"
          :items-per-page="15"
          sort-by="id"
          class="table-kitchen-sink"
        >
          <!-- product -->
          <template #[`item.name`]="{item}">
            <div class="d-flex align-center">
              <div class="d-flex flex-column ms-3">
                <span class="d-block font-weight-semibold text-truncate text--primary">{{ item.name }}</span>
              </div>
            </div>
          </template>

          <template #[`item.id`]="{item}">
            <div class="d-flex align-center">
              <div class="d-flex flex-column ms-3">
                <span class="d-block font-weight-semibold text-truncate text--primary">{{ orderNum(item) }}</span>
              </div>
            </div>
          </template>

          <template #[`item.created_at`]="{item}">
            <div class="d-flex align-center">
              <div class="d-flex flex-column ms-3">
                <span class="d-block font-weight-semibold text-truncate text--primary">{{ moment(item.created_at).format('D MMMM YYYY')  }}</span>
              </div>
            </div>
          </template>

          <template #[`item.updated_at`]="{item}">
            <div class="d-flex align-center">
              <div class="d-flex flex-column ms-3">
                <span class="d-block font-weight-semibold text-truncate text--primary">{{ moment(item.updated_at).format('D MMMM YYYY')  }}</span>
              </div>
            </div>
          </template>

          <!-- action -->
          <template #[`item.actions`]="{ item }">
            <div class="col-12 w-">
              <v-btn
                outlined
                x-small
                color="info"
                @click="editItem(item)"
              >
                <v-icon
                  x-small
                  class="me-2"
                >
                  {{ icons.mdiPencilOutline }}
                </v-icon>
                Modifier
              </v-btn>

              <v-btn
                class="ml-1"
                outlined
                x-small
                color="success"
                @click="goToEditRole(item)"
              >
                <v-icon
                  small
                >
                  {{ icons.mdiPencilOutline }}
                </v-icon>
                Affecter des permissions
              </v-btn>

            </div>
          </template>
        </v-data-table>

        <!-- dialog édition -->
        <v-dialog
          v-model="dialog"
          max-width="500px"
        >
          <v-card>
            <v-card-title>
              <span>Modifier un rôle</span>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <v-container>
                <v-form
                  ref="updateRole"
                >
                <v-row>
                  <v-col
                    cols="12"
                      offset-md="2"
                      md="8"
                  >
                    <v-text-field
                      v-model="editedItem.name"
                        label="Nom"
                        :rules="LocalNameRules"
                      outlined
                      dense
                        placeholder="Nom"
                        hide-details="auto"
                    ></v-text-field>
                  </v-col>
                </v-row>
                </v-form>
              </v-container>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="error"
                outlined
                @click="close"
              >
                Annuler
              </v-btn>
              <v-btn
                color="primary"
                @click="save"
              >
                Enregistrer
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- dialog suppression -->
        <v-dialog
          v-model="dialogDelete"
          max-width="500px"
        >
          <v-card>
            <v-card-title>
              Are you sure you want to delete this item?
            </v-card-title>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="error"
                outlined
                @click="closeDelete"
              >
                Cancel
              </v-btn>
              <v-btn
                color="success"
                @click="deleteItemConfirm"
              >
                OK
              </v-btn>
              <v-spacer></v-spacer>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>
    </v-card>
  </div>
</template>

<script>
import { ref, onBeforeMount } from '@vue/composition-api'
import {
  mdiMagnify,
  mdiDeleteOutline,
  mdiPencilOutline,
  mdiEyeOutline,
} from '@mdi/js'
import data from '@/views/locaux/datatable'
import Http from '@/helpers/http'
import { useRouter } from '@core/utils'
import Vue from 'vue'
import moment from 'moment'
// eslint-disable-next-line import/extensions
import SnackView from '@/views/SnackView'

Vue.prototype.moment = moment

export default {
  components: {
    SnackView,
  },
  setup() {
    const search = ref('')
    const { router } = useRouter()
    const roles = ref([])
    const snackbar = ref({
      active: false,
      message: '',
      color: 'success',
    })

    // Règle de validation d'un champ
    const LocalNameRules = [
      v => !!v || 'Ce champ est réquis',
    ]
    const goToEditRole = role => {
      // eslint-disable-next-line no-shadow,radix
      const roleId = parseInt(role.id)

      router.push({ name: 'edit-role', params: { id: roleId } })
    }

    const orderNum = item => roles.value.indexOf(item) + 1

    const loadRoles = () => {
      Http.get('role')
        .then(async response => {
          if (response.status === 200) {
            roles.value = await response.data
          }
        })
        .catch(error => {
          console.error('Erreur :', error)
        })
    }

    onBeforeMount(() => {
      loadRoles()
    })

    return {
      icons: {
        mdiMagnify,
        mdiDeleteOutline,
        mdiPencilOutline,
        mdiEyeOutline,
      },
      search,
      goToEditRole,
      loadRoles,
      LocalNameRules,
      orderNum,
      headers: [
        { text: 'N°', value: 'id', divider: true },
        { text: 'Nom', value: 'name', divider: true },
        { text: 'Date de création', value: 'created_at', divider: true },
        { text: 'Dernière MAJ', value: 'updated_at', divider: true },
        {
          text: 'Actions', value: 'actions', sortable: false, divider: true,
        },
      ],
      dataOptions: {
        sortBy: ['id'],
      },
      roles,
      editedIndex: -1,
      editedItem: {},
      defaultItem: {},
      dialog: false,
      dialogDelete: false,
      snackbar,
    }
  },
  watch: {
    dialog(val) {
      // eslint-disable-next-line no-unused-expressions
      val || this.close()
    },
    dialogDelete(val) {
      // eslint-disable-next-line no-unused-expressions
      val || this.closeDelete()
    },
  },
  mounted() {
    moment.locale('fr')
  },
  methods: {
    initialize() {
      this.userList = JSON.parse(JSON.stringify(data))
    },

    editItem(item) {
      console.log(item)
      this.editedIndex = this.roles.indexOf(item)
      // eslint-disable-next-line no-param-reassign
      this.editedItem = { ...item }
      this.dialog = true
    },

    deleteItem(item) {
      this.editedIndex = this.roles.indexOf(item)
      this.editedItem = { ...item }
      this.dialogDelete = true
    },

    deleteItemConfirm() {
      this.roles.value.splice(this.editedIndex, 1)
      this.closeDelete()
    },

    close() {
      this.dialog = false
      this.$nextTick(() => {
        this.editedItem = { ...this.defaultItem }
        this.editedIndex = -1
      })
    },

    closeDelete() {
      this.dialogDelete = false
      this.$nextTick(() => {
        this.editedItem = { ...this.defaultItem }
        this.editedIndex = -1
      })
    },

    save() {
      if (this.$refs.updateRole.validate()) {
      console.log(this.editedItem)
        this.snackbar.active = false
        Http.put(`role/${this.editedItem.id}`, this.editedItem)
        .then(response => {
            if (response.status === 201) {
              this.loadRoles()
              this.snackbar.message = response.message
              this.snackbar.active = true
            }
            console.log(response.message)
        })
        .catch(error => {
          console.log(error)
        })

      this.close()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.table-kitchen-sink ::v-deep {
  .v-data-table-header {
    white-space: nowrap;
  }
}
</style>
